import { useState, useEffect } from 'react';
import { useAuthStore } from '../../../store/authStore';
import { useSubscriptionStore } from '../../../store/subscriptionStore';

interface Position {
  x: number;
  y: number;
  pageX: number;
  pageY: number;
  viewportWidth: number;
  viewportHeight: number;
  pageUrl: string;
  elementSelector?: string;
  isFixedElement: boolean;
}

export const useCommentMode = () => {
  const [isCommentMode, setIsCommentMode] = useState(false);
  const [commentFormPosition, setCommentFormPosition] = useState<Position | null>(null);
  const [cursorPosition, setCursorPosition] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const { user, isGuest } = useAuthStore();
  const { isSubscribed, isTrialActive } = useSubscriptionStore();

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsCommentMode(false);
        setCommentFormPosition(null);
      }
    };

    if (isCommentMode) {
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [isCommentMode]);

  useEffect(() => {
    if (!user && !isGuest && isCommentMode) {
      setIsCommentMode(false);
      setCommentFormPosition(null);
    }
  }, [user, isGuest, isCommentMode]);

  const getElementSelector = (element: Element): string | undefined => {
    try {
      if (element.id) {
        return `#${element.id}`;
      }
      
      const className = Array.from(element.classList)
        .filter(cls => cls && !cls.startsWith('hover:') && !cls.startsWith('focus:'))
        .slice(0, 3)
        .join('.');
      
      if (className) {
        const tagName = element.tagName.toLowerCase();
        return `${tagName}.${className}`;
      }
      
      return element.tagName.toLowerCase();
    } catch {
      return undefined;
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isCommentMode && !commentFormPosition) {
      const target = e.target as HTMLElement;
      
      if (!target.closest('[data-comment-cursor]') && 
          !target.closest('#feedbackflow-toolbar') && 
          !target.closest('[data-comment-marker]') &&
          !target.closest('[data-comment-form]')) {
        
        setCursorPosition({ x: e.clientX, y: e.clientY });
      }
    }
  };

  const handlePageClick = (e: React.MouseEvent) => {
    if (!isCommentMode) return;
    
    if (!user && !isGuest) return;

    if (!isSubscribed && !isTrialActive && !isGuest) return;

    const target = e.target as HTMLElement;
    
    if (target.closest('#feedbackflow-toolbar') || 
        target.closest('[data-comment-marker]') ||
        target.closest('[data-comment-form]')) {
      return;
    }

    const scrollX = window.scrollX || window.pageXOffset;
    const scrollY = window.scrollY || window.pageYOffset;
    
    const viewportX = e.clientX;
    const viewportY = e.clientY;
    
    // For now, treat ALL elements as regular elements (not fixed)
    // This will make comments scroll with the page content
    const isFixedElement = false;
    
    console.log('🔍 Element check (forcing regular):', {
      target: target.tagName,
      targetClasses: target.className,
      isFixedElement,
      viewportX,
      viewportY,
      scrollX,
      scrollY
    });
    
    // For fixed elements, use viewport coordinates
    // For regular elements, use page coordinates
    let pageX, pageY;
    
    if (isFixedElement) {
      // Fixed element: use viewport coordinates directly
      pageX = viewportX;
      pageY = viewportY;
    } else {
      // Regular element: add scroll offset
      pageX = viewportX + scrollX;
      pageY = viewportY + scrollY;
    }

    const pageUrl = window.location.href;
    const elementSelector = getElementSelector(target);

    setCommentFormPosition({
      x: viewportX,
      y: viewportY,
      pageX,
      pageY,
      viewportWidth: window.innerWidth,
      viewportHeight: window.innerHeight,
      pageUrl,
      elementSelector,
      isFixedElement, // Add flag to track fixed positioning
    });
  };

  return {
    isCommentMode,
    setIsCommentMode,
    commentFormPosition,
    setCommentFormPosition,
    cursorPosition,
    handleMouseMove,
    handlePageClick,
  };
};